import { useEffect, useState } from 'react';
import { getSignatureUrl } from 'api/signatures';
import { loadSignature } from 'utils/offlineSignaturesStore';

const VACIO = { url: null, loading: false, pendiente: false, firmada: false };

const esKeyS3 = (v) => v.startsWith('signatures/');
const esUrlAbsoluta = (v) => /^(https?:|data:|blob:)/.test(v);
// Paths legacy (pre-migración a S3): empiezan con "/" o "." y solo los resuelve el API estático.
const esPathLegacy = (v) => v.startsWith('/') || v.startsWith('.');

/**
 * Resuelve el `value` de un campo de firma a algo mostrable.
 *
 * - key S3 (`signatures/...`) → URL presigned.
 * - URL absoluta → tal cual.
 * - path legacy → firmada, sin imagen.
 * - cualquier otra cadena es un `localId` de una firma capturada sin conexión:
 *   el blob está en IndexedDB y se muestra desde ahí, marcada como pendiente
 *   de sincronizar. Si ya no está (el daemon la subió y borró) queda firmada sin imagen.
 *
 * @param {string|undefined|null} value
 * @returns {{ url: string|null, loading: boolean, pendiente: boolean, firmada: boolean }}
 */
export function useFirmaPreview(value) {
  const [state, setState] = useState(() => inicial(value));

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    if (!value || typeof value !== 'string') {
      setState(VACIO);
      return undefined;
    }
    if (esUrlAbsoluta(value)) {
      setState({ ...VACIO, url: value, firmada: true });
      return undefined;
    }
    if (esPathLegacy(value)) {
      setState({ ...VACIO, firmada: true });
      return undefined;
    }

    setState({ ...VACIO, loading: true, firmada: true });

    if (esKeyS3(value)) {
      getSignatureUrl(value)
        .then((url) => !cancelled && setState({ ...VACIO, url, firmada: true }))
        .catch(() => !cancelled && setState({ ...VACIO, firmada: true }));
    } else {
      loadSignature(value)
        .then((rec) => {
          if (cancelled) return;
          if (rec?.blob) {
            objectUrl = URL.createObjectURL(rec.blob);
            setState({ ...VACIO, url: objectUrl, pendiente: true, firmada: true });
          } else {
            setState({ ...VACIO, firmada: true });
          }
        })
        .catch(() => !cancelled && setState({ ...VACIO, firmada: true }));
    }

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [value]);

  return state;
}

// Primer render sin parpadeo: lo que se puede saber sin async.
function inicial(value) {
  if (!value || typeof value !== 'string') return VACIO;
  if (esUrlAbsoluta(value)) return { ...VACIO, url: value, firmada: true };
  if (esPathLegacy(value)) return { ...VACIO, firmada: true };
  return { ...VACIO, loading: true, firmada: true };
}

export default useFirmaPreview;
